<template>
  <div
    class="refonte-item-file"
    v-bind:class="props.class">
    <div class="item-file-content-left">
      <div class="item-file-content-title">
        <div class="item-file-content-title-text">
          <div class="item-file-content-title-text-top">
            {{ props.title }}
          </div>
          <div
            v-if="props.subtitle"
            class="item-file-content-title-text-bottom">
            {{ props.subtitle }}
          </div>
        </div>
      </div>
    </div>
    <div class="item-file-content-middle">
      <div class="item-file-content-status">
        <div
          v-bind:class="`item-file-content-status-text ${props.convention.signee ? 'text-jinius-green' : 'text-refonte-secondary'}`">
          <div class="item-file-content-status-text-icon">
            <IconValidate20Green
              class="text-jinius-green"
              v-if="props.convention.signee" />
            <IconTime
              class="text-refonte-secondary"
              v-else />
          </div>
          <span>{{
            props.convention.signee
              ? 'Signée'
              : signatureEnCours
                ? 'En cours de signature'
                : 'En attente de signature'
          }}</span>
        </div>
      </div>
    </div>
    <div class="item-file-content-right">
      <template v-if="!props.convention.signee && !signatureEnCours">
        <refonte-button
          v-if="props.on_click_download"
          class="item-file-content-download"
          variant="secondary"
          v-bind:icon_left="IconDownload"
          v-on:click="props.on_click_download"
          v-tooltip="'Télécharger la version non signée'">
        </refonte-button>
        <refonte-button
          class="item-file-content-sign"
          variant="secondary"
          v-bind:icon_left="IconOpenNewTab"
          v-on:click="sign">
          Signer électroniquement
        </refonte-button>
      </template>
      <template v-else>
        <refonte-button
          v-if="props.on_click_download && props.convention.signee"
          class="item-file-content-download"
          variant="secondary"
          v-bind:icon_left="IconDownload"
          description="la version signée"
          v-tooltip="'Télécharger la version signée'"
          v-on:click="props.on_click_download">
        </refonte-button>
        <refonte-button
          variant="secondary"
          v-bind:icon_left="IconPlusCircle"
          v-on:click="openModal">
          Voir détails
        </refonte-button>
      </template>
    </div>
  </div>
  <refonte-modal-file-select-signataire
    v-if="isSelectSignataireModalOpen"
    v-bind:open_signature_manuelle="openSignatureManuelle"
    header_title="Signer électroniquement votre convention"
    v-bind:show="isSelectSignataireModalOpen"
    v-bind:on_close="() => setIsSelectSignataireModalOpen(false)"
    v-bind:on_submit="onCloseSuccess"
    v-bind:file_title="props.title"
    v-bind:formation_title="props.formation_title"
    v-bind:participant_nom_prenom="
      props.convention.nomPrenomParticipant ??
      (props.convention.typeId == 2 ? props.participant_nom_prenom : '')
    "
    v-bind:session_date="props.session_date"
    v-bind:launch_url="`client/v2/${props.client_id}/convention/${props.convention.id}/signature`"
    v-bind:client_id="props.client_id" />
  <refonte-modal-file-signature-details-signed
    document="La convention"
    v-bind:title="
      props.convention.signee ? 'Convention signée' : 'Signature en cours'
    "
    v-bind:show_body="
      !!(props.convention.signee && props.convention?.signature)
    "
    v-bind:email="props.convention?.signature?.email"
    v-bind:nom_prenom="props.convention?.signature?.signataireNomPrenom"
    v-bind:date="props.convention?.signature?.createdDate"
    v-bind:show="isDetailsSignedModalOpen"
    v-bind:on_close="() => setIsDetailsSignedModalOpen(false)" />
  <refonte-modal-file-signature-details-in-progress
    v-bind:cancel_url="`client/v2/${props.client_id}/convention/${props.convention.id}/signature`"
    v-bind:signature_maintenance="props.convention?.signatureMaintenance"
    v-bind:date="props.convention.lancement?.createdDate"
    v-bind:email="props.convention.lancement?.email"
    v-bind:show_body="!!props.convention.lancement"
    v-bind:show="isDetailsInProgressModalOpen"
    v-bind:client_id="props.client_id"
    v-bind:on_close="closeInProgressModal"
    v-bind:on_submit="props.on_submit" />
  <refonte-modal-file-signature-details-not-configured
    v-bind:show="isSignatureNotConfiguredModalOpen"
    v-bind:on_close="() => setIsSignatureNotConfiguredModalOpen(false)"
    v-bind:organisateur_email="props.convention?.organisateurEmail" />
  <refonte-modal-file-signature-details-maintenance
    v-bind:show="isSignatureMaintenanceModalOpen"
    v-bind:on_close="() => setIsSignatureMaintenanceModalOpen(false)" />
</template>

<script setup lang="ts">
  import IconDownload from '@/assets/img/refonte/icons/display/icon-download.svg?component';
  import IconOpenNewTab from '@/assets/img/refonte/icons/action/icon-open-new-tab.svg?component';
  import IconPlusCircle from '@/assets/img/refonte/icons/display/icon-plus-circle.svg?component';
  import RefonteButton from '@/components/refonte/buttons/refonte-button.vue';
  import IconTime from '@/assets/img/refonte/icons/information/icon-time.svg?component';
  import IconValidate20Green from '@/assets/img/refonte/icons/information/icon-validate-20-green.svg?component';
  import { ref, onMounted, computed } from 'vue';
  import { ConventionDocument } from '@/types/gestion/inscriptions-types';
  import RefonteModalFileSelectSignataire from '@/components/refonte/modals/refonte-modal-file-select-signataire.vue';
  import RefonteModalFileSignatureDetailsSigned from '@/components/refonte/modals/refonte-modal-file-signature-details-signed.vue';
  import RefonteModalFileSignatureDetailsInProgress from '@/components/refonte/modals/refonte-modal-file-signature-details-in-progress.vue';
  import RefonteModalFileSignatureDetailsNotConfigured from '@/components/refonte/modals/refonte-modal-file-signature-details-not-configured.vue';
  import RefonteModalFileSignatureDetailsMaintenance from '@/components/refonte/modals/refonte-modal-file-signature-details-maintenance.vue';

  const props = withDefaults(
    defineProps<{
      title: string;
      subtitle?: string;
      convention: ConventionDocument;
      on_click_download?: Function;
      formation_title?: string;
      session_date?: string;
      participant_nom_prenom?: string;
      client_id?: number;
      class?: string;
      on_close?: Function;
      on_submit?: Function;
      open_signature_manuelle?: Function;
    }>(),
    {
      class: ''
    }
  );
  const input = ref(null);

  onMounted(() => {
    if (input.value) {
      input.value.focus();
    }
  });
  const isDetailsSignedModalOpen = ref<boolean>(false);
  const isSignatureNotConfiguredModalOpen = ref<boolean>(false);
  const isSignatureMaintenanceModalOpen = ref<boolean>(false);
  const isDetailsInProgressModalOpen = ref<boolean>(false);
  const isSelectSignataireModalOpen = ref<boolean>(false);

  const signatureEnCours = computed(
    () => props.convention.statutSignatureId == 1
  );

  const openModal = () => {
    if (signatureEnCours.value) {
      setIsDetailsInProgressModalOpen(true);
    } else if (props.convention.statutSignatureId == 2) {
      setIsDetailsSignedModalOpen(true);
    }
  };
  const setIsDetailsSignedModalOpen = (value: boolean) => {
    isDetailsSignedModalOpen.value = value;
  };

  const closeInProgressModal = async () => {
    await setIsDetailsInProgressModalOpen(false);
  };

  const onCloseSuccess = async () => {
    await setIsSelectSignataireModalOpen(false);
    if (props.on_submit) {
      props.on_submit();
    }
    if (props.on_close) {
      props.on_close();
    }
  };
  const setIsDetailsInProgressModalOpen = (value: boolean) => {
    isDetailsInProgressModalOpen.value = value;
  };
  function setIsSelectSignataireModalOpen(value: boolean) {
    isSelectSignataireModalOpen.value = value;
  }
  const setIsSignatureMaintenanceModalOpen = (value: boolean) => {
    isSignatureMaintenanceModalOpen.value = value;
  };

  const setIsSignatureNotConfiguredModalOpen = (value: boolean) => {
    isSignatureNotConfiguredModalOpen.value = value;
  };
  const openSignatureManuelle = () => {
    setIsSelectSignataireModalOpen(false);
    if (props.open_signature_manuelle) {
      props.open_signature_manuelle();
    }
  };
  const sign = () => {
    if (props.convention?.signatureNotConfigured) {
      setIsSignatureNotConfiguredModalOpen(true);
    } else if (props.convention?.signatureMaintenance) {
      setIsSignatureMaintenanceModalOpen(true);
    } else {
      setIsSelectSignataireModalOpen(true);
    }
  };
</script>

<style lang="scss" scoped>
  .refonte-item-file {
    @apply flex flex-grow bg-refonte-pale-violet-2;
    gap: 4px;
    border-radius: 8px;
    padding: 6px 12px 6px 12px;
    max-width: 700px;
    width: 100%;
    min-height: 48px;

    .item-file-content-left {
      @apply flex items-center;
      gap: 4px;
      width: 30%;
      .item-file-content-title {
        @apply flex flex-grow items-start;
        gap: 2px;

        .item-file-content-title-text {
          @apply flex flex-grow flex-col items-start;
          gap: 2px;

          .item-file-content-title-text-top {
            @apply self-stretch overflow-hidden text-ellipsis text-refonte-black;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 1;
            font-size: 13px;
            font-weight: 400;
            line-height: 16px;
          }
          .item-file-content-title-text-bottom {
            @apply self-stretch overflow-hidden text-ellipsis text-refonte-black-40;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 1;
            font-size: 12px;
            font-weight: 400;
            line-height: 14px;
          }
        }
      }
    }
    .item-file-content-middle {
      @apply flex items-center;
      width: 30%;
      gap: 4px;

      .item-file-content-status {
        @apply flex flex-grow items-center;
        gap: 4px;

        .item-file-content-status-text {
          @apply flex flex-grow items-center;
          gap: 2px;
          margin-right: 6px;

          .item-file-content-status-text-icon {
            @apply flex shrink-0 items-center justify-center;
            width: 20px;
            height: 20px;

            :deep(svg) {
              width: 18px;
              height: 18px;
            }
          }

          span {
            @apply whitespace-nowrap;
            font-size: 13px;
            line-height: 16px;
            font-weight: 400;
          }
        }
      }
    }
    .item-file-content-right {
      @apply flex justify-end;
      width: 40%;
      gap: 6px;

      .item-file-content-download {
        gap: 2px;
      }
      .item-file-content-sign {
        gap: 8px;
      }
    }

    @media (max-width: 515.98px) {
      @apply flex-col items-stretch;

      .item-file-content-left {
        max-width: 100%;

        .item-file-content-download-separator {
          @apply hidden;
        }
      }
    }
  }
</style>
